<script setup lang="ts">
  import { BottomSheet, Radio, RadioSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { ref } from 'vue';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import { SERP_SORT_ORDER, SERP_SORT_RADIO_NAME } from '../config/sort';
  import { SERP_SORT_LABELS, SERP_SORT_TEXTS } from '../config/texts';
  import type { SerpSort } from '../model/types';

  interface SerpSortSheetProps {
    modelValue: SerpSort;
  }

  const props = defineProps<SerpSortSheetProps>();

  interface SerpSortSheetEmits {
    (e: 'update:modelValue', value: SerpSort): void;
    (e: 'select', value: SerpSort): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<SerpSortSheetEmits>();

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  // Сортировка меняется по «Применить», поэтому радиокнопки правят черновик, а не выдачу.
  const draft = ref<SerpSort>(props.modelValue);

  const onSelect = (value: SerpSort): void => {
    draft.value = value;
    emit('select', value);
  };

  const onApply = async (): Promise<void> => {
    emit('update:modelValue', draft.value);
    await bottomSheetRef.value?.close();
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    class="serp-sort-sheet"
    data-testid="serp-sort-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="serp-sort-sheet__sheet"
      :animated="true"
      :icon-closable="true"
      @close="emit('close')"
    >
      <template #header>
        <Typo
          class="serp-sort-sheet__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="serp-sort-sheet-title"
        >
          {{ SERP_SORT_TEXTS.title }}
        </Typo>
      </template>

      <div class="serp-sort-sheet__options">
        <Radio
          v-for="option in SERP_SORT_ORDER"
          :key="option"
          :model-value="draft"
          :value="option"
          :name="SERP_SORT_RADIO_NAME"
          :label="SERP_SORT_LABELS[option]"
          :size="RadioSize.L"
          :mobile="true"
          :data-testid="`serp-sort-sheet-option-${option}`"
          @update:modelValue="onSelect"
        />
      </div>

      <template #footer>
        <MobilePrimaryButton
          testid="serp-sort-sheet-apply"
          @click="onApply"
        >
          {{ SERP_SORT_TEXTS.apply }}
        </MobilePrimaryButton>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .serp-sort-sheet {
    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }

    &__options {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
    }
  }
</style>
